<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealStageBadge from 'dashboard/routes/dashboard/dealBoard/components/DealStageBadge.vue';
import { STAGES, formatMoney } from 'dashboard/routes/dashboard/dealBoard/constants';

const { t } = useI18n();
const route = useRoute();

const dealsByContact = useMapGetter('contactDeals/getDealsByContactId');
const uiFlags = useMapGetter('contactDeals/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const contactDeals = computed(() => dealsByContact.value(route.params.contactId));

const STAGE_LABEL_KEYS = {
  pre: 'PRE_LISTING',
  active: 'ACTIVE',
  uc: 'UNDER_CONTRACT',
  closed: 'CLOSED',
  vacant: 'VACANT',
  leased: 'LEASED',
};

const stageMeta = deal => STAGES[deal.stage] || STAGES.pre;
const stageLabel = deal =>
  t(`DEAL_BOARD.STAGES.${STAGE_LABEL_KEYS[deal.stage] || 'PRE_LISTING'}`);

const amountFor = deal =>
  deal.deal_type === 'Lease' ? formatMoney(deal.rent) : formatMoney(deal.price);

const dealBoardUrl = deal =>
  frontendURL(`accounts/${route.params.accountId}/deal-board`, {
    q: deal.address,
  });
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="contactDeals.length > 0" class="flex flex-col gap-3 px-6 py-4">
    <a
      v-for="deal in contactDeals"
      :key="deal.id"
      :href="dealBoardUrl(deal)"
      target="_blank"
      rel="noopener noreferrer"
      class="flex flex-col gap-2 rounded-xl border border-n-weak p-3 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="truncate text-sm font-semibold text-n-slate-12">
          {{ deal.address }}
        </span>
        <DealStageBadge
          :label="stageLabel(deal)"
          :badge-class="stageMeta(deal).badge"
          :dot-class="stageMeta(deal).dot"
        />
      </div>
      <div class="flex items-center justify-between text-xs text-n-slate-10">
        <span class="truncate">{{ deal.city }}</span>
        <span class="font-semibold text-n-slate-12">{{ amountFor(deal) }}</span>
      </div>
    </a>
  </div>
  <p v-else class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.DEALS.EMPTY_STATE') }}
  </p>
</template>
